import { PlanName } from "@/apollo/types";
import { getEnvironment } from "@/helpers/environment";
import { AccountType, useCurrentUser } from "@/providers/UserProvider";
import dayjs from "@/utils/date";

export type UserAttribute = {
  email: string;
  name: string;
  user_id: string;
  // Custom attributes
  workspace_id?: string;
  workspace_name?: string;
  workspace_slug?: string;
  workspace_plan?: string;
  environment?: string;
};

export type CompanyAttribute = {
  company_id: string;
  created_at: number;
  name: string;
  plan?: string;
  // Custom attributes
  workspace_slug: string;
  company_environment?: string;
};

const createUser = (
  user: ReturnType<typeof useCurrentUser>,
  account?: AccountType,
  planName?: PlanName,
): UserAttribute => ({
  email: user.email,
  name: `${user.firstName} ${user.lastName}`,
  user_id: user.id,
  // Custom User Attributes for Intercom
  workspace_id: account?.id,
  workspace_name: account?.name,
  workspace_slug: account?.slug,
  workspace_plan: planName?.toUpperCase(),
});

const createCompany = (
  account: AccountType,
  planName?: PlanName,
): CompanyAttribute => ({
  company_id: account.id,
  created_at: dayjs(account.createdAt).unix(),
  name: account.name,
  plan: planName?.toUpperCase(),
  workspace_slug: account.slug,
});

const enhanceUserForEnvironment = (user: UserAttribute): UserAttribute => {
  const env = getEnvironment();
  if (env === "production") return user;

  // Insert +env before the @ to handle emails that may already contain +
  const email = user.email.replace(/@/, `+${env}@`);
  return {
    ...user,
    email,
    name: `${user.name} (${env})`,
    environment: env,
  };
};

const enhanceCompanyForEnvironment = (
  company: CompanyAttribute,
): CompanyAttribute => {
  const env = getEnvironment();
  if (env === "production") return company;

  return {
    ...company,
    name: `${company.name} (${env})`,
    company_environment: env,
  };
};

/** The attribute payload Intercom is given for the signed-in user. */
export function buildIntercomAttributes(
  user: ReturnType<typeof useCurrentUser>,
  account?: AccountType,
  planName?: PlanName,
): Record<string, unknown> {
  return {
    ...enhanceUserForEnvironment(createUser(user, account, planName)),
    ...(account
      ? {
          company: enhanceCompanyForEnvironment(
            createCompany(account, planName),
          ),
        }
      : {}),
  };
}
